import { Phone, Mail, MapPin, MessageCircle, Navigation, Clock, AlertTriangle } from "lucide-react";
import { useSettings } from "../contexts/SettingsContext";
import OpenBadge from "../components/OpenBadge";

export default function Contact() {
  const { settings, waLink, status } = useSettings();
  const s = settings || {};
  const hasWhatsApp = Boolean((s.whatsapp_number || "").trim());
  const today = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"][new Date().getDay()];

  return (
    <div className="container-oxy py-10 sm:py-14" data-testid="contact-page">
      <p className="eyebrow">Contact & infos</p>
      <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Nous sommes là pour vous</h1>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <div className="flex flex-col gap-5">
          <div className="card-oxy p-6 sm:p-8">
            <OpenBadge />
            <div className="mt-5 space-y-4 text-sm">
              {s.address ? (
                <p className="flex items-start gap-3 text-slate-700">
                  <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-gold-dark" />
                  <span>{s.address}{s.city ? `, ${s.city}` : ""}</span>
                </p>
              ) : (
                <p className="flex items-start gap-3 text-slate-400"><MapPin className="mt-0.5 h-5 w-5 shrink-0" />Adresse en cours de configuration</p>
              )}
              {s.phone && (
                <a href={`tel:${s.phone}`} data-testid="contact-phone-link" className="flex items-center gap-3 font-semibold text-slate-800 hover:text-gold-dark">
                  <Phone className="h-5 w-5 shrink-0 text-gold-dark" />{s.phone}
                </a>
              )}
              {s.email && (
                <a href={`mailto:${s.email}`} data-testid="contact-email-link" className="flex items-center gap-3 text-slate-700 hover:text-gold-dark">
                  <Mail className="h-5 w-5 shrink-0 text-gold-dark" />{s.email}
                </a>
              )}
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              {s.phone && (
                <a href={`tel:${s.phone}`} data-testid="contact-call-btn" className="btn-ink !px-5 !py-2.5 text-sm"><Phone className="h-4 w-4" /> Appeler</a>
              )}
              {hasWhatsApp && (
                <a href={waLink("Bonjour Oxyservices, j'ai une question.")} target="_blank" rel="noopener noreferrer"
                  data-testid="contact-whatsapp-btn" className="btn-gold !px-5 !py-2.5 text-sm"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
              )}
              {s.maps_address && (
                <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(s.maps_address)}`}
                  target="_blank" rel="noopener noreferrer" data-testid="contact-directions-btn"
                  className="btn-outline-oxy !px-5 !py-2.5 text-sm"><Navigation className="h-4 w-4" /> Itinéraire</a>
              )}
            </div>
          </div>

          <div className="card-oxy p-6 sm:p-8" data-testid="contact-hours">
            <h2 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
              <Clock className="h-5 w-5 text-gold-dark" /> Horaires d'ouverture
            </h2>
            <ul className="mt-4 divide-y divide-slate-100 text-sm">
              {(s.hours || []).map((h) => (
                <li key={h.day} data-testid={`hours-${h.day}`}
                  className={`flex items-center justify-between py-2.5 ${h.day === today ? "font-bold text-ink" : "text-slate-600"}`}>
                  <span className="capitalize">{h.day}{h.day === today && <span className="ml-2 rounded-full bg-gold-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-gold-dark">Aujourd'hui</span>}</span>
                  <span>{h.open ? `${h.start} – ${h.end}${h.break_start ? ` · pause ${h.break_start}–${h.break_end}` : ""}` : "Fermé"}</span>
                </li>
              ))}
            </ul>
            {(s.closures || []).length > 0 && (
              <div className="mt-4 rounded-xl bg-amber-50 p-3 text-xs text-amber-800" data-testid="contact-closures">
                <p className="flex items-center gap-1.5 font-bold"><AlertTriangle className="h-3.5 w-3.5" /> Fermetures exceptionnelles</p>
                <ul className="mt-1.5 space-y-1">
                  {s.closures.map((c, i) => <li key={i}>{c.date} — {c.reason}</li>)}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-3xl border border-amber-500/20 shadow-sm" data-testid="contact-map-wrapper">
          {s.maps_address ? (
            <iframe title="Plan Oxyservices" data-testid="contact-map" className="h-full min-h-[420px] w-full" loading="lazy"
              src={`https://www.google.com/maps?q=${encodeURIComponent(s.maps_address)}&output=embed`} />
          ) : (
            <div className="flex h-full min-h-[420px] flex-col items-center justify-center gap-2 bg-slate-100 text-slate-400">
              <MapPin className="h-10 w-10" />
              <p className="max-w-xs text-center text-sm">La carte Google Maps s'affichera ici une fois l'adresse configurée dans l'espace admin.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
